'use client';

import { useState, useEffect, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { JSONPath } from 'jsonpath-plus';
import { Card, CardBody, Input, Button, Tooltip } from '@nextui-org/react';
import { Copy } from 'lucide-react';
import { useTheme } from '@/app/ThemeProvider';
import { useEditorPrefs } from '@/app/EditorPrefsProvider';
import ResizableSplit from '@/components/ResizableSplit';
import EditorToolbar from '@/components/Editor/EditorToolbar';
import { trackToolAction } from '@/lib/analytics';

const Editor = dynamic(() => import('@monaco-editor/react'), {
  ssr: false,
  loading: () => (
    <div className="w-full h-[600px] flex items-center justify-center bg-gray-50 dark:bg-slate-800 animate-pulse" role="status" aria-label="Loading editor">
      <span className="text-sm text-gray-400 dark:text-gray-500">Loading editor…</span>
    </div>
  ),
});

const defaultJson = `{
  "store": {
    "book": [
      {
        "category": "fiction",
        "author": "Nigel Rees",
        "title": "Sayings of the Century",
        "price": 8.95
      },
      {
        "category": "fiction",
        "author": "Evelyn Waugh",
        "title": "Sword of Honour",
        "price": 12.99
      },
      {
        "category": "fiction",
        "author": "Herman Melville",
        "title": "Moby Dick",
        "isbn": "0-553-21311-3",
        "price": 8.99
      },
      {
        "category": "fiction",
        "author": "J. R. R. Tolkien",
        "title": "The Lord of the Rings",
        "isbn": "0-395-19395-8",
        "price": 22.99
      }
    ],
    "bicycle": {
      "color": "red",
      "price": 19.95
    }
  },
  "expensive": 10
}`;

const exampleExpressions = [
  { label: 'Root object', value: '$' },
  { label: 'All books', value: '$.store.book[*]' },
  { label: 'All book titles', value: '$.store.book[*].title' },
  { label: 'First book', value: '$.store.book[0]' },
  { label: 'Last book', value: '$.store.book[-1]' },
  { label: 'First two books', value: '$.store.book[0:2]' },
  { label: 'Books with ISBN', value: '$.store.book[?(@.isbn)]' },
  { label: 'Books cheaper than $10', value: '$.store.book[?(@.price < 10)]' },
  { label: 'All prices', value: '$..price' }
];

export default function JsonPathEvaluator() {
  const [jsonInput, setJsonInput] = useState(defaultJson);
  const [jsonPath, setJsonPath] = useState('$');
  const [result, setResult] = useState<string>('');
  const [matchCount, setMatchCount] = useState<number>(0);
  const [error, setError] = useState<string>('');
  const [showHelp, setShowHelp] = useState(false);
  const [showExamples, setShowExamples] = useState(false);
  const { theme } = useTheme();
  const monacoTheme = theme === 'dark' ? 'vs-dark' : 'light';
  const { wordWrap, minimap, fontSize } = useEditorPrefs();

  const evaluateJSONPath = useCallback(() => {
    try {
      setError('');
      const json = JSON.parse(jsonInput);
      const results = JSONPath({ path: jsonPath, json });

      if (results.length === 0) {
        setError('No matches found for the given JSONPath expression');
        setResult('');
        setMatchCount(0);
      } else {
        setResult(JSON.stringify(results, null, 2));
        setMatchCount(results.length);
      }
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError('Invalid JSON input: ' + err.message);
      } else if (err instanceof Error) {
        setError('Invalid JSONPath expression: ' + err.message);
      } else {
        setError('An unexpected error occurred');
      }
      setResult('');
      setMatchCount(0);
    }
  }, [jsonInput, jsonPath]);

  // Evaluate whenever JSON input or JSONPath expression changes
  useEffect(() => {
    if (jsonInput.trim() && jsonPath.trim()) {
      evaluateJSONPath();
    }
  }, [jsonInput, jsonPath, evaluateJSONPath]);

  const handleCopy = (text: string) => {
    trackToolAction("jsonpath", "copy");
    navigator.clipboard.writeText(text);
    // Could add toast notification here if desired
  };

  const resetToDefaultJson = () => {
    setJsonInput(defaultJson);
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-[1fr,auto,auto] gap-2 items-center mb-4">
          <Input
            label="JSONPath Expression"
            placeholder="Enter JSONPath expression (e.g., $.store.book[*].title)"
            value={jsonPath}
            onChange={(e) => {
              trackToolAction('jsonpath', 'edit');
              setJsonPath(e.target.value);
            }}
            className="w-full"
            endContent={
              <Tooltip content="Copy expression">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  radius="full"
                  aria-label="Copy JSONPath expression"
                  onClick={() => handleCopy(jsonPath)}
                >
                  <Copy size={16} />
                </Button>
              </Tooltip>
            }
          />
          <Button
            color="primary"
            variant="flat"
            onClick={() => {
              const newShowHelp = !showHelp;
              setShowHelp(newShowHelp);
              if (newShowHelp) setShowExamples(false);
            }}
          >
            {showHelp ? 'Hide Help' : 'Show Help'}
          </Button>
          <Button
            color="primary"
            variant="flat"
            onClick={() => {
              const newShowExamples = !showExamples;
              setShowExamples(newShowExamples);
              if (newShowExamples) setShowHelp(false);
            }}
          >
            {showExamples ? 'Hide Examples' : 'Show Examples'}
          </Button>
        </div>

        {showHelp && (
          <Card className="mb-4">
            <CardBody>
              <div className="mb-3">
                <a href="https://goessner.net/articles/JsonPath/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  JSONPath Documentation →
                </a>
              </div>
              <h3 className="text-lg font-semibold mb-2">JSONPath Syntax Guide</h3>
              <div className="text-sm space-y-2">
                <p><code>$</code> - Root object</p>
                <p><code>@</code> - Current object</p>
                <p><code>.</code> - Child operator</p>
                <p><code>..</code> - Recursive descent (searches all descendants)</p>
                <p><code>*</code> - Wildcard (all elements in an object or array)</p>
                <p><code>[n]</code> - Array index (0-based)</p>
                <p><code>[-n]</code> - Array index from the end (e.g., [-1] is the last element)</p>
                <p><code>[start:end]</code> - Array slice</p>
                <p><code>[?(@.property)]</code> - Filter expression (elements with the specified property)</p>
                <p><code>[?(@.property==value)]</code> - Filter expression with comparison</p>
              </div>
            </CardBody>
          </Card>
        )}

        {showExamples && (
          <Card className="mb-4">
            <CardBody>
              <h3 className="text-lg font-semibold mb-2">Example Expressions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {exampleExpressions.map((example, index) => (
                  <Button 
                    key={index} 
                    size="sm" 
                    variant="bordered"
                    onClick={() => setJsonPath(example.value)}
                    className="justify-start text-left"
                  >
                    <div>
                      <div className="font-medium">{example.label}</div>
                      <div className="text-xs opacity-70 font-mono">{example.value}</div>
                    </div>
                  </Button>
                ))}
              </div>
            </CardBody>
          </Card>
        )}
      </div>

      <div className="flex justify-end mb-2">
        <EditorToolbar />
      </div>

      <ResizableSplit
        storageKey="split-jsonpath"
        leftLabel="input JSON"
        rightLabel="query results"
        left={
        <Card>
          <CardBody>
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-semibold">Input JSON</h2>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="bordered"
                  color="primary"
                  onClick={resetToDefaultJson}
                >
                  Reset to Example
                </Button>
                <Button
                  size="sm"
                  variant="bordered"
                  startContent={<Copy size={16} />}
                  onClick={() => handleCopy(jsonInput)}
                >
                  Copy
                </Button>
              </div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 p-2 rounded-t-lg text-sm">
              <span>Edit your JSON data below or use the example</span>
            </div>
            <Editor
              height="600px"
              theme={monacoTheme}
              defaultLanguage="json"
              value={jsonInput}
              onChange={(value) => {
                trackToolAction('jsonpath', 'edit');
                setJsonInput(value || '');
              }}
              options={{
                minimap: { enabled: minimap },
                fontSize,
                wordWrap: wordWrap ? 'on' : 'off',
                lineNumbers: 'on',
                folding: true,
                formatOnPaste: true,
                formatOnType: true,
              }}
            />
          </CardBody>
        </Card>
        }
        right={
        <Card>
          <CardBody>
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-semibold">Query Results</h2>
              {result && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="bordered"
                    startContent={<Copy size={16} />}
                    onClick={() => handleCopy(result)}
                  >
                    Copy Results
                  </Button>
                  <Button
                    size="sm"
                    variant="bordered"
                    startContent={<Copy size={16} />}
                    onClick={() => handleCopy(jsonPath)}
                  >
                    Copy Query
                  </Button>
                </div>
              )}
            </div>

            {error ? (
              <div className="p-4 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 rounded-lg">
                <p className="font-medium mb-1">Error</p>
                <p>{error}</p>
              </div>
            ) : result ? (
              <>
                <div className="bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 p-2 rounded-t-lg text-sm">
                  <div className="flex justify-between items-center">
                    <div>
                      Query: <code className="bg-green-100 dark:bg-green-900 px-1 py-0.5 rounded">{jsonPath}</code>
                    </div>
                    <div>
                      Found {matchCount} match{matchCount !== 1 ? 'es' : ''}
                    </div>
                  </div>
                </div>
                <Editor
                  height="600px"
                  theme={monacoTheme}
                  defaultLanguage="json"
                  value={result}
                  options={{
                    readOnly: true,
                    minimap: { enabled: minimap },
                    fontSize,
                    wordWrap: wordWrap ? 'on' : 'off',
                    lineNumbers: 'on',
                    folding: true,
                  }}
                />
              </>
            ) : (
              <div className="p-4 bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-gray-400 rounded-lg">
                Enter a JSONPath expression and valid JSON to see results
              </div>
            )}
          </CardBody>
        </Card>
        }
      />
    </div>
  );
}